import XDate from 'xdate'
import {
  faEnvelopeOpenText,
  faFloppyDisk,
  faGear,
  faReceipt,
  faScroll,
  faTruck,
} from '@fortawesome/pro-duotone-svg-icons'

import {
  QuickPassProfile,
  QuickPassUnitPreferences,
} from '../../_types/quickpass-profile'
import { QuickActionDescriptor } from './types'

const QUICK_ACTIONS: QuickActionDescriptor[] = [
  {
    to: '/profile',
    icon: faGear,
    title: 'Tune preferences',
    subtitle: "Update price, beds, cities — anything that's shifted.",
  },
  {
    to: '/saved-listings',
    icon: faFloppyDisk,
    title: 'Saved listings',
    subtitle: "Everything you've bookmarked across the network.",
  },
  {
    to: '/move-prep',
    icon: faTruck,
    title: 'Move-prep checklist',
    subtitle: 'Countdown + tasks to knock out before move-in.',
  },
  {
    to: '/activity',
    icon: faScroll,
    title: 'Activity feed',
    subtitle: 'Every price drop, import, and milestone in order.',
  },
  {
    to: '/lease-check',
    icon: faReceipt,
    title: 'Lease check',
    subtitle: 'AI flags risky and unusual clauses before you sign.',
  },
  {
    to: '/pings',
    icon: faEnvelopeOpenText,
    title: 'Pings',
    subtitle: 'Anonymous match-only nudges from agents about real listings.',
  },
]

const dashboardContainerMotion = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
}

const dashboardItemMotion = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.32 } },
}

/**
 * Picks a time-of-day-aware greeting prefix.
 *
 * @returns {string}
 */
const getTimeOfDayGreeting = (): string => {
  const hours = new XDate().getHours()

  if (hours < 5) {
    return 'Burning the midnight oil'
  }

  if (hours < 12) {
    return 'Good morning'
  }

  if (hours < 17) {
    return 'Good afternoon'
  }

  return 'Good evening'
}

const formatPrice = (value: number): string => {
  return `$${value.toLocaleString('en-CA')}`
}

/**
 * Formats the price range into a single human string. Handles missing
 * bounds gracefully (no min, no max, both, neither).
 *
 * @param {number | null} priceMin
 * @param {number | null} priceMax
 * @returns {string}
 */
const formatPriceRange = (
  priceMin: number | null,
  priceMax: number | null,
): string => {
  if (priceMin === null && priceMax === null) {
    return 'Any budget'
  }

  if (priceMin === null) {
    return `Up to ${formatPrice(priceMax as number)}`
  }

  if (priceMax === null) {
    return `${formatPrice(priceMin)}+`
  }

  return `${formatPrice(priceMin)} – ${formatPrice(priceMax)}`
}

const formatBedroomCounts = (counts: number[]): string => {
  if (counts.length === 0) {
    return 'Any bedrooms'
  }

  const parts = counts.map(count => (count === 0 ? 'Studio' : `${count}`))
  return parts.length === 1 ? `${parts[0]} bed` : parts.join(', ')
}

const formatPropertyTypes = (
  types: QuickPassUnitPreferences['propertyTypes'],
): string => {
  if (types.length === 0) {
    return 'Any place'
  }

  return types
    .map(type => `${type[0].toUpperCase()}${type.slice(1)}`)
    .join(', ')
}

const formatDwellingScope = (
  dwellingScope: QuickPassUnitPreferences['dwellingScope'],
): string => {
  if (dwellingScope === 'whole-property') {
    return 'Whole place'
  }

  if (dwellingScope === 'unit') {
    return 'A unit'
  }

  return 'Either works'
}

const formatCityList = (cities: string[]): string => {
  if (cities.length === 0) {
    return 'Anywhere'
  }

  if (cities.length === 1) {
    return cities[0]
  }

  if (cities.length === 2) {
    return `${cities[0]} & ${cities[1]}`
  }

  return `${cities[0]}, ${cities[1]} +${cities.length - 2} more`
}

/**
 * Reads a friendly first-name from the profile, falling back to a generic
 * greeting if the profile is missing or anonymous.
 *
 * @param {QuickPassProfile | null} profile
 * @returns {string}
 */
const getDisplayFirstName = (profile: QuickPassProfile | null): string => {
  if (profile === null || profile.firstName === '') {
    return 'there'
  }

  return profile.firstName
}

export {
  QUICK_ACTIONS,
  dashboardContainerMotion,
  dashboardItemMotion,
  getTimeOfDayGreeting,
  formatPriceRange,
  formatBedroomCounts,
  formatPropertyTypes,
  formatDwellingScope,
  formatCityList,
  getDisplayFirstName,
}
